// Mock data for the study UI demos. The live study uses the same scenario text
// but receives record values from the backend study agent.

const STUDY_ASSET_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/study-assets/brief-accident-v2"
    : "/study-assets/brief-accident-v2";

export const FIELD_DEFS = [
  { id: "description_of_accident", label: "What happened", type: "multiline_text" },
  { id: "date_of_accident", label: "Date", type: "date" },
  { id: "time_of_accident", label: "Time", type: "time" },
  { id: "location_of_accident", label: "Location", type: "text" },
  { id: "road_conditions", label: "Road conditions", type: "text" },
  { id: "number_of_vehicles_involved", label: "Number of vehicles", type: "number" },
  { id: "vehicles.role", label: "Vehicle role", type: "text" },
  { id: "vehicles.vehicle_description", label: "Vehicle description", type: "text" },
  { id: "vehicles.damage_description", label: "Vehicle damage", type: "text" },
  { id: "property_damage", label: "Other property damage", type: "text" },
  { id: "injuries", label: "Injuries", type: "text" },
  { id: "witnesses", label: "Witnesses", type: "text" },
];

export const SCENARIO = {
  title: "Scenario",
  intro:
    "Use the details in this scenario while completing the accident report. You may reread it at any time.",
  audio: {
    label: "Listen to the scenario",
    src: `${STUDY_ASSET_BASE}/incident-report.mp3`,
  },
  image: {
    alt: "Overhead accident visualization for Main St and Oak Ave",
    src: `${STUDY_ASSET_BASE}/accident-visualization-main-oak-2026-07-02.png`,
  },
  facts: [
    {
      label: "What happened",
      value:
        "You were driving your blue Toyota Corolla in the right lane of Main St, preparing to turn right onto Oak Ave. A red two-door car moved from the left lane into your lane to turn right before the traffic light changed. Its right side scraped the left side of your car. You braked hard, slid on the wet road toward the curb, and the front-right corner of your car hit a traffic-light pole.",
      segments: [{
        sourceFragmentId: "source.what_happened.narrative",
        value:
          "You were driving your blue Toyota Corolla in the right lane of Main St, preparing to turn right onto Oak Ave. A red two-door car moved from the left lane into your lane to turn right before the traffic light changed. Its right side scraped the left side of your car. You braked hard, slid on the wet road toward the curb, and the front-right corner of your car hit a traffic-light pole.",
        fieldIds: ["description_of_accident"],
      }],
    },
    {
      label: "When and where",
      value: "July 2, 2026, around 2:35 PM, at Main St and Oak Ave in Springfield.",
      segments: [{
        sourceFragmentId: "source.when_where.summary",
        value: "July 2, 2026, around 2:35 PM, at Main St and Oak Ave in Springfield.",
        fieldIds: ["date_of_accident", "time_of_accident", "location_of_accident"],
      }],
    },
    {
      label: "Road conditions",
      value: "The road was wet because light rain had just stopped.",
      segments: [{
        sourceFragmentId: "source.road_conditions.summary",
        value: "The road was wet because light rain had just stopped.",
        fieldIds: ["road_conditions"],
      }],
    },
    {
      label: "Vehicles",
      value:
        "Two vehicles were involved. Vehicle 1 was your blue Toyota Corolla, plate AB12 CDE. Vehicle 2 was a red two-door car; you do not know its plate.",
      segments: [
        {
          sourceFragmentId: "source.vehicles.count",
          value: "Two vehicles were involved. ",
          fieldIds: ["number_of_vehicles_involved"],
        },
        {
          sourceFragmentId: "source.vehicles.vehicle_1",
          value: "Vehicle 1 was your blue Toyota Corolla, plate AB12 CDE. ",
          fieldIds: [
            "number_of_vehicles_involved",
            "vehicles[1].role",
            "vehicles[1].vehicle_description",
          ],
        },
        {
          sourceFragmentId: "source.vehicles.vehicle_2",
          value: "Vehicle 2 was a red two-door car; you do not know its plate.",
          fieldIds: [
            "number_of_vehicles_involved",
            "vehicles[2].role",
            "vehicles[2].vehicle_description",
          ],
        },
      ],
    },
    {
      label: "Damage and driveability",
      value:
        "Your Toyota Corolla had scratches on the left side and a dented front-right bumper from hitting the traffic-light pole. It was still drivable afterward and was not towed. The red two-door car had scratches on its right side and was also able to drive away. The traffic-light pole was bent.",
      segments: [
        {
          sourceFragmentId: "source.damage.vehicle_1",
          value:
            "Your Toyota Corolla had scratches on the left side and a dented front-right bumper from hitting the traffic-light pole. It was still drivable afterward and was not towed. ",
          fieldIds: ["vehicles[1].damage_description"],
        },
        {
          sourceFragmentId: "source.damage.vehicle_2",
          value:
            "The red two-door car had scratches on its right side and was also able to drive away. ",
          fieldIds: ["vehicles[2].damage_description"],
        },
        {
          sourceFragmentId: "source.damage.property",
          value: "The traffic-light pole was bent.",
          fieldIds: ["property_damage"],
        },
      ],
    },
    {
      label: "People",
      value: "No one was hurt. A witness named Sam Lee gave the phone number 555-0100.",
      segments: [
        {
          sourceFragmentId: "source.people.injuries",
          value: "No one was hurt. ",
          fieldIds: ["injuries"],
        },
        {
          sourceFragmentId: "source.people.witness",
          value: "A witness named Sam Lee gave the phone number 555-0100.",
          fieldIds: ["witnesses"],
        },
      ],
    },
  ],
};

export const SOURCE_EVIDENCE_VERSION = "source-evidence-map-v1";

export function sourceEvidenceForField(
  fieldId,
  sourceFragmentIds = [],
  scenario = SCENARIO
) {
  const requested = new Set(
    Array.isArray(sourceFragmentIds) ? sourceFragmentIds.filter(Boolean) : []
  );
  const matches = [];
  for (const fact of scenario?.facts || []) {
    for (const segment of fact.segments || []) {
      const sourceFragmentId = segment.sourceFragmentId || segment.source_fragment_id;
      const fieldIds = segment.fieldIds || segment.field_ids || [];
      if (
        requested.has(sourceFragmentId)
        || (!requested.size && fieldIds.includes(fieldId))
      ) {
        matches.push({
          sourceFragmentId,
          label: fact.label,
          value: segment.value,
        });
      }
    }
  }
  return matches;
}

// Demo record with the same three seeded mistakes defined in study/assets/brief-accident-v2/seeded-errors.json:
// location_of_accident, vehicles[1].damage_description, and property_damage.
export const MOCK_RECORD = {
  description_of_accident:
    "I was driving my blue Toyota Corolla in the right lane on Main St, preparing to turn right onto Oak Ave. A red two-door car moved from the left lane into my lane, its right side scraped the left side of my car, and I braked hard on the wet road before the front-right corner of my car hit a traffic-light pole.",
  date_of_accident: "2026-07-02",
  time_of_accident: "14:35",
  location_of_accident: "Main St and Pine Ave, Springfield",
  road_conditions: "Wet road after light rain had just stopped",
  number_of_vehicles_involved: 2,
  "vehicles[1].role": "My vehicle",
  "vehicles[1].vehicle_description": "My blue Toyota Corolla, plate AB12 CDE",
  "vehicles[1].damage_description": "My vehicle had a dented front-right bumper and scratches on the left side; it was unable to drive afterward and was not towed",
  "vehicles[2].role": "The other car that moved from the left lane into my lane",
  "vehicles[2].vehicle_description": "The other vehicle was a red two-door car; I did not know its plate",
  "vehicles[2].damage_description": "The other vehicle had scratches on its right side and was able to drive away",
  property_damage: "No non-vehicle property damage",
  injuries: "No one was hurt",
  witnesses: "Sam Lee, 555-0100",
};

export const MOCK_EDITS = [
  { field_id: "description_of_accident", value: MOCK_RECORD.description_of_accident, source: "active" },
  { field_id: "date_of_accident", value: MOCK_RECORD.date_of_accident, source: "passive" },
  { field_id: "time_of_accident", value: MOCK_RECORD.time_of_accident, source: "passive" },
  { field_id: "location_of_accident", value: MOCK_RECORD.location_of_accident, source: "passive" },
  { field_id: "road_conditions", value: MOCK_RECORD.road_conditions, source: "passive" },
  { field_id: "number_of_vehicles_involved", value: MOCK_RECORD.number_of_vehicles_involved, source: "passive" },
  { field_id: "vehicles[1].role", value: MOCK_RECORD["vehicles[1].role"], source: "passive" },
  { field_id: "vehicles[1].vehicle_description", value: MOCK_RECORD["vehicles[1].vehicle_description"], source: "passive" },
  { field_id: "vehicles[1].damage_description", value: MOCK_RECORD["vehicles[1].damage_description"], source: "passive" },
  { field_id: "vehicles[2].role", value: MOCK_RECORD["vehicles[2].role"], source: "passive" },
  { field_id: "vehicles[2].vehicle_description", value: MOCK_RECORD["vehicles[2].vehicle_description"], source: "passive" },
  { field_id: "vehicles[2].damage_description", value: MOCK_RECORD["vehicles[2].damage_description"], source: "passive" },
  { field_id: "property_damage", value: MOCK_RECORD.property_damage, source: "passive" },
  { field_id: "injuries", value: MOCK_RECORD.injuries, source: "passive" },
  { field_id: "witnesses", value: MOCK_RECORD.witnesses, source: "passive" },
];

export const MOCK_CHAT = [
  { sender: "bot", text: "Briefly describe what happened." },
  {
    sender: "participant",
    text:
      "I was driving my blue Toyota Corolla in the right lane on Main St, preparing to turn right onto Oak Ave. A red two-door car moved from the left lane into my lane, scraped the left side of my car, and I braked hard before the front-right corner of my car hit a traffic-light pole.",
  },
  { sender: "bot", text: "When and where did this happen, and what were the road conditions?" },
  {
    sender: "participant",
    text: "July 2, 2026, around 2:35 PM, at Main St and Oak Ave in Springfield. The road was wet because light rain had just stopped.",
  },
  { sender: "bot", text: "How many vehicles were involved, and what should I record for each one?" },
  {
    sender: "participant",
    text:
      "There were two vehicles. Vehicle 1 was my blue Toyota Corolla, plate AB12 CDE, with scratches on the left side and a dented front-right bumper. It was still drivable and was not towed. Vehicle 2 was a red two-door with unknown plate and scratches on its right side, and it could drive away. The pole was bent, no one was hurt, and Sam Lee witnessed it at 555-0100.",
  },
];

export const TIMELINE = [
  { kind: "msg", sender: "bot", text: "Briefly describe what happened." },
  {
    kind: "msg",
    sender: "participant",
    text:
      "I was driving my blue Toyota Corolla in the right lane on Main St, preparing to turn right onto Oak Ave. A red two-door car moved from the left lane into my lane, scraped the left side of my car, and I braked hard before the front-right corner of my car hit a traffic-light pole.",
  },
  { kind: "edit", field_id: "description_of_accident", value: MOCK_RECORD.description_of_accident, source: "active" },
  { kind: "msg", sender: "bot", text: "When and where did this happen, and what were the road conditions?" },
  {
    kind: "msg",
    sender: "participant",
    text: "July 2, 2026, around 2:35 PM, at Main St and Oak Ave in Springfield. The road was wet because light rain had just stopped.",
  },
  { kind: "edit", field_id: "date_of_accident", value: MOCK_RECORD.date_of_accident, source: "active" },
  { kind: "edit", field_id: "time_of_accident", value: MOCK_RECORD.time_of_accident, source: "passive" },
  { kind: "edit", field_id: "location_of_accident", value: MOCK_RECORD.location_of_accident, source: "passive" },
  { kind: "edit", field_id: "road_conditions", value: MOCK_RECORD.road_conditions, source: "passive" },
  { kind: "msg", sender: "bot", text: "How many vehicles were involved, and what should I record for each one?" },
  {
    kind: "msg",
    sender: "participant",
    text:
      "There were two vehicles. Vehicle 1 was my blue Toyota Corolla, plate AB12 CDE, with scratches on the left side and a dented front-right bumper. It was still drivable and was not towed. Vehicle 2 was a red two-door with unknown plate and scratches on its right side, and it could drive away. The pole was bent, no one was hurt, and Sam Lee witnessed it at 555-0100.",
  },
  { kind: "edit", field_id: "number_of_vehicles_involved", value: MOCK_RECORD.number_of_vehicles_involved, source: "active" },
  { kind: "edit", field_id: "vehicles[1].role", value: MOCK_RECORD["vehicles[1].role"], source: "passive" },
  { kind: "edit", field_id: "vehicles[1].vehicle_description", value: MOCK_RECORD["vehicles[1].vehicle_description"], source: "passive" },
  { kind: "edit", field_id: "vehicles[1].damage_description", value: MOCK_RECORD["vehicles[1].damage_description"], source: "passive" },
  { kind: "edit", field_id: "vehicles[2].role", value: MOCK_RECORD["vehicles[2].role"], source: "passive" },
  { kind: "edit", field_id: "vehicles[2].vehicle_description", value: MOCK_RECORD["vehicles[2].vehicle_description"], source: "passive" },
  { kind: "edit", field_id: "vehicles[2].damage_description", value: MOCK_RECORD["vehicles[2].damage_description"], source: "passive" },
  { kind: "edit", field_id: "property_damage", value: MOCK_RECORD.property_damage, source: "passive" },
  { kind: "edit", field_id: "injuries", value: MOCK_RECORD.injuries, source: "passive" },
  { kind: "edit", field_id: "witnesses", value: MOCK_RECORD.witnesses, source: "passive" },
];

export const labelFor = (id) => (FIELD_DEFS.find((f) => f.id === id)?.label || id);
export const defFor = (id) => FIELD_DEFS.find((f) => f.id === id);

export const formatValue = (v) => {
  if (v === null || v === undefined || v === "" || v === "open" || v === "<open>") {
    return "(not filled)";
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};
